// =============================================================================
// selectors/review.ts — the seam between the Review screen and everything under
// it.
// -----------------------------------------------------------------------------
// `ui/` may not import `domain/` (D47), so this is the only door. It also does
// the thing a selector exists for: it decides SCOPE.
//
// ── 🚨 THE DATE RANGE IS THE REVIEW'S OWN, NOT THE GLOBAL FILTER'S ──
//
// Every other surface in this client reads the range in the toolbar. This one
// does not, and the old client says why in its own source: *"Reviews are scoped
// to their own week/month period — bypass the global date filter so the review
// still works when the user has set a different global range (e.g. 'Today' while
// reviewing last week)."*
//
// ⚠ THE ACCOUNT FILTER **DOES** APPLY. Beta reads `tradesForActiveAccounts()`,
// so the two halves of the toolbar are answered differently on this tab — the
// dates are ignored and the accounts are honoured. That asymmetry is easy to
// "tidy" in either direction and both directions are wrong: dropping the account
// filter would total a funded account and a sim together; applying the date
// filter would empty the tab whenever the toolbar says "Today".
//
// ⚠ CONTRAST WITH BEST OPPS, which applies NEITHER (D178). "Which scope" is a
// per-tab question, and guessing consistently is how a port diverges
// consistently.
//
// ── 🚨 THE NUMBERS ARE ALWAYS IN DOLLARS ──
//
// Beta calls `computeKpis(trades)` with no unit, and the default is `usd`. So
// this tab ignores the display-unit toggle that the Dashboard and Analytics
// honour. Passed explicitly here rather than left to a default, because a tab
// that silently disagrees with the unit switch is the kind of thing a reader
// assumes is a bug and "fixes".
//
// 🚨 AND THAT IS EXACTLY WHAT HAPPENED (#67). This paragraph was written, and
// then a later pass wired the toggle in anyway — with a describe block asserting
// the new behaviour, so the suite went green defending the opposite of what the
// file says. The brief was the visible symptom: it prints `$` on every figure,
// so on Points it stated a points total to the trader AND to the AI coach as
// "+\$249.11". But the tiles and the pattern cards were converting too.
//
// ⚠ SO THE UNIT IS NO LONGER A PARAMETER OF THIS VIEW. Removing the field is
// the point: a caller cannot pass one by accident, and the next reader who
// thinks this tab looks unfinished has to change a signature to do it.
// =============================================================================

import { computeKpis, type Kpis, type KpiTrade } from '../domain/kpis';
import {
  buildReviewBrief, findReview, newReview, reviewPeriod, reviewSummary,
  type Review, type ReviewBriefInput, type ReviewCadence, type ReviewJournalEntry,
  type ReviewPeriod, type ReviewSummary, type ReviewTrade,
} from '../domain/review';
import { effectiveTradeDate } from '../domain/session';
import { countsForMetrics, tradeValue, type ContractSpec } from '../domain/trade';
import type { DayBoundaryMode } from '../domain/range';
import {
  metricsContextFactory, tradesForSelection,
  type AccountFilterAccount, type AccountSelection,
} from './accounts';
import { loadJournalRange } from '../io/sync/journal';
import { loadReviews, saveReview, type SaveReviewResult } from '../io/sync/reviews';
import type { SupabaseClient } from '@supabase/supabase-js';

export type {
  Review, ReviewCadence, ReviewJournalEntry, ReviewSummary, SaveReviewResult,
};
// ⚠ Same mechanical rule the other selectors state: `ui/` may not import
// `domain/` at all, and naming one of these in a prop type still counts. A form
// that cannot name its own input ends up typing it `any`, which is how a
// component starts reading fields the model does not promise.
export type { Kpis } from '../domain/kpis';
export {
  isReviewDate, promptSections, REFLECTION_PROMPTS, reviewPeriod, reviewPeriodLabel,
  reviewSavedLabel, shiftReviewDate,
  type MistakeTally, type PatternTally, type ReflectionPrompt, type ReviewAdherence,
  type ReviewBriefKpis, type ReviewLevels, type ReviewSelfAssessment,
} from '../domain/review';
export type { ReviewBriefInput, ReviewPeriod };

/** What this tab needs from a trade. Every field is already on the table read. */
export interface ReviewViewTrade extends KpiTrade {
  accountId: string | null;
  sessionDate: string | null;
  patternId: string | null;
  mistakeTags: readonly string[] | null;
  booleans: Record<string, unknown> | null;
  annotated: boolean | null | undefined;
}

export interface ReviewView<T extends ReviewViewTrade = ReviewViewTrade> {
  /** Rows the read already narrowed to the period — see the header. */
  trades: readonly T[];
  accounts: readonly AccountFilterAccount[];
  contracts: readonly ContractSpec[];
  /** Empty means "all accounts" — not "no accounts". */
  selection: AccountSelection;
  mode: DayBoundaryMode;
  patternName: (id: string) => string | null;
}

export interface ReviewNumbers {
  kpis: Kpis;
  summary: ReviewSummary;
}

/**
 * The eight tiles and the four cards, over ONE population.
 *
 * 🚨 THE PREDICATE IS DEFINED ONCE AND USED TWICE, and that is the whole reason
 * this does not simply call `selectKpis`. The KPI strip and the pattern/mistake/
 * adherence cards must be answering about the same set of trades: if the tiles
 * counted a sim account's trades and the cards did not, the screen would show a
 * win rate over ten trades beside a pattern table built from six, and nothing on
 * it would say so. D102 is that failure on two surfaces; this is it on one.
 *
 * The two-step account rule is `selectors/kpis`': `tradesForSelection` decides
 * what is IN VIEW, `countsForMetrics` decides what may MOVE A NUMBER, and they
 * are not redundant — a mixed selection containing a SIM account keeps its trades
 * listed and out of the totals.
 */
export function selectReviewNumbers<T extends ReviewViewTrade>(
  view: ReviewView<T>,
): ReviewNumbers {
  const inView = tradesForSelection(view.trades, view.selection, view.accounts);
  const ctxFor = metricsContextFactory(view.accounts, view.selection);
  const counts = (t: T): boolean => countsForMetrics(t, ctxFor(t));

  // ⚠ `'usd'` LITERALLY, not a default and not the caller's choice. See header.
  const kpis = computeKpis(inView, 'usd', view.contracts, counts);

  const population: ReviewTrade[] = inView.filter(counts).map((t) => ({
    // ⚠ THE EFFECTIVE DATE, never the raw one. Nothing in `reviewSummary` reads
    // it today, but handing a summary a field that says `date` and holds the
    // calendar one is exactly how four of D130's seven divergences happened.
    date: effectiveTradeDate(t, view.mode) || null,
    annotated: t.annotated,
    booleans: t.booleans,
    mistakeTags: t.mistakeTags,
    // ⚠ DOLLARS, like the strip above them. The two must agree, and the strip
    // is what beta prints — see the header.
    value: tradeValue(t, 'usd', view.contracts),
    patternId: t.patternId,
  }));

  return { kpis, summary: reviewSummary(population, view.patternName) };
}

/**
 * 🚨 ARE THE ROWS ON HAND ACTUALLY THIS PERIOD'S?
 *
 * The trades read keeps the PREVIOUS window's rows while the next read is in
 * flight, so a tab does not blink to empty on every step. This tab changes its
 * own window with four buttons, so without this check it renders one frame of
 * the old period's eight tiles under the new period's heading — and they read as
 * that period's numbers.
 *
 * ⚠ MEASURED on the running app, 2026-08-27: pressing `Monthly` showed the
 * week's `+$249.11 · 4 trades` beneath the heading `August 2026`, before
 * settling on the month's `+$316.85 · 19 trades`.
 *
 * ⚠ A `phase === 'loading'` check is NOT equivalent. That flag is set inside an
 * effect, which runs after the render that changed the window — so there is
 * always one frame where the phase still says `ready` over the wrong rows.
 */
export function rowsCoverPeriod(
  loaded: { startDate: string | null; endDate: string | null } | null,
  period: ReviewPeriod | null,
): boolean {
  if (!loaded || !period) return false;
  return loaded.startDate === period.start && loaded.endDate === period.end;
}

/**
 * The review row for a period, or a fresh unsaved one.
 *
 * 🚨 MATCHED BY PERIOD, AND THE NEW ID IS MINTED BY THE CALLER (D179). The old
 * client's `${cadence}-${periodStart}` was the same string for every user over a
 * global primary key, and locked everyone but the first out of that week forever.
 */
export function selectReview(
  reviews: readonly Review[] | null,
  cadence: ReviewCadence,
  dateIso: string,
  newId: string,
): Review {
  const period = reviewPeriod(cadence, dateIso);
  return findReview(reviews ?? [], cadence, period.start)
    ?? newReview(newId, cadence, period);
}

/**
 * Journal entries inside the period, oldest first.
 *
 * ⚠ FILTERED ON THE ENTRY'S OWN DATE, which is beta's choice and is right here:
 * a journal entry is already keyed by the session day it belongs to (D86/D87), so
 * there is no second date to reconcile.
 */
export function selectReviewJournals<T extends { date: string }>(
  entries: readonly T[], periodStart: string, periodEnd: string,
): readonly T[] {
  return entries
    .filter((j) => j.date && j.date >= periodStart && j.date <= periodEnd)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** The markdown both the clipboard and the coach get. One string, two buttons. */
export function buildBrief(input: ReviewBriefInput): string {
  return buildReviewBrief(input);
}

export function fetchReviews(sb: SupabaseClient): Promise<Review[]> {
  return loadReviews(sb);
}

/**
 * The period's journal entries, for the brief only.
 *
 * ⚠ READ ON A PRESS, NOT ON MOUNT. Nothing on the Review SCREEN shows a journal
 * entry — they exist in the brief the coach and the clipboard get — so paying for
 * this read every time the tab opens would be a request per page view for content
 * most visits never use.
 */
export function fetchReviewJournals(
  sb: SupabaseClient, periodStart: string, periodEnd: string,
): Promise<ReviewJournalEntry[]> {
  return loadJournalRange(sb, periodStart, periodEnd);
}

export function storeReview(
  sb: SupabaseClient, userId: string, r: Review,
): Promise<SaveReviewResult> {
  return saveReview(sb, userId, r);
}
